import { prefersReducedMotion } from '@theme/utilities';

class NumbersBlock extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number} */
  #frame = 0;

  /** @type {boolean} */
  #hasAnimated = false;

  connectedCallback() {
    this.#syncSizer();

    if (prefersReducedMotion()) {
      this.#render(this.#target);
      this.classList.add('is-in-view');
      return;
    }

    this.#render(0);
    this.classList.add('is-ready');
    this.#observe();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;

    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }
  }

  get #target() {
    const value = Number(this.dataset.value);
    return Number.isFinite(value) ? value : 0;
  }

  get #duration() {
    const value = Number(this.dataset.duration);
    return Number.isFinite(value) && value > 0 ? value : 2000;
  }

  get #decimals() {
    const value = Number(this.dataset.decimals);
    return Number.isFinite(value) && value > 0 ? Math.min(value, 6) : 0;
  }

  get #locale() {
    const locale = this.dataset.locale;
    if (!locale || locale.endsWith('-')) return undefined;
    return locale;
  }

  get #valueEl() {
    return this.querySelector('.numbers-block__value');
  }

  get #sizerEl() {
    return this.querySelector('.numbers-block__sizer');
  }

  #observe() {
    const target = this.closest('.shopify-section') ?? this;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#animate();
        this.#observer?.disconnect();
        this.#observer = null;
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    this.#observer.observe(target);
  }

  #animate() {
    if (this.#hasAnimated) return;
    this.#hasAnimated = true;
    this.classList.add('is-in-view');

    const start = performance.now();
    const from = 0;
    const to = this.#target;
    const duration = this.#duration;

    /** @param {number} now */
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      this.#render(from + (to - from) * eased);

      if (progress < 1) {
        this.#frame = requestAnimationFrame(tick);
        return;
      }

      this.#render(to);
      this.#frame = 0;
    };

    this.#frame = requestAnimationFrame(tick);
  }

  /**
   * @param {number} value
   */
  #format(value) {
    return new Intl.NumberFormat(this.#locale, {
      minimumFractionDigits: this.#decimals,
      maximumFractionDigits: this.#decimals,
    }).format(value);
  }

  /**
   * @param {number} value
   */
  #render(value) {
    const valueEl = this.#valueEl;
    if (!valueEl) return;
    valueEl.textContent = this.#format(value);
  }

  #syncSizer() {
    const sizerEl = this.#sizerEl;
    if (!sizerEl) return;
    sizerEl.textContent = this.#format(this.#target);
  }
}

if (!customElements.get('numbers-block')) {
  customElements.define('numbers-block', NumbersBlock);
}
